import React from 'react';
import { useTranslation } from 'react-i18next';
import SelectInput from 'shared/components/inputs/SelectInput';
import RequiredTag from 'shared/components/forms/RequiredTag';
import EarthRangerSiteInput from 'connections/EarthRangerSiteInput';
import { AccountBaseUrl, GeneralDraft, GeneralFields } from './generalFields';
import { inputClasses } from 'styles/tailwindClassnames';

const rowClasses =
  'flex w-full flex-col gap-2 md:flex-row md:items-start md:justify-between md:gap-6';
const fieldClasses = (error?: string) =>
  `${error ? inputClasses.error : inputClasses.standard} w-full`;

const FieldLabel = ({
  htmlFor,
  label,
  help,
  required,
}: {
  htmlFor: string;
  label: string;
  help: string;
  required: boolean;
}) => (
  <div className="flex flex-col md:w-1/2">
    <label htmlFor={htmlFor} className="mr-1">
      {label}
      {required && <RequiredTag size="sm" />}
    </label>
    <span className="mt-1 text-body-sm text-ui-text-primary-text">{help}</span>
  </div>
);

const FieldError = ({ error }: { error?: string }) =>
  error ? (
    <p className="mt-1 text-body-sm text-ui-status-error-destructive">
      {error}
    </p>
  ) : null;

// The account's General pane: name, workspace and, where the technology needs
// one, the address Gundi reaches it at.
const AccountGeneralForm = ({
  fields,
  urlState,
  onEdit,
  organizations,
  onLoadMoreOrgs,
  onSearchOrgs,
}: {
  fields: GeneralFields;
  urlState: AccountBaseUrl;
  onEdit: (change: GeneralDraft) => void;
  organizations?: any[];
  onLoadMoreOrgs?: () => void;
  onSearchOrgs?: (search: string) => void;
}) => {
  const { t } = useTranslation();
  const { name, owner, url, nameError, ownerError, urlError } = fields;
  return (
    <div className="rounded border border-ui-field-outline bg-white p-4">
      <div
        className={`mb-2 border-b border-ui-divider-lines pb-3 ${rowClasses}`}
      >
        <FieldLabel
          htmlFor="account_name"
          label={t('Name')}
          help={t('A descriptive name for this Connection.')}
          required
        />
        <div className="md:w-1/2">
          <input
            id="account_name"
            data-testid="ConfigurationsTab-account-name-input"
            value={name}
            onChange={(e) => onEdit({ name: e.target.value })}
            className={fieldClasses(nameError)}
          />
          <FieldError error={nameError} />
        </div>
      </div>
      <div
        className={`mb-2 border-b border-ui-divider-lines pb-3 ${rowClasses}`}
      >
        <FieldLabel
          htmlFor="account_workspace"
          label={t('Workspace')}
          help={t('The workspace this Connection belongs to.')}
          required
        />
        <div className="md:w-1/2">
          <SelectInput
            inputId="account_workspace"
            options={(organizations || []).map((org: any) => ({
              label: org.name,
              value: org.id,
            }))}
            className={'basic-single'}
            value={owner?.id ? { label: owner.name, value: owner.id } : null}
            isDisabled={false}
            isLoading={false}
            isClearable={false}
            isSearchable={true}
            isMulti={false}
            name={t('Workspace')}
            hasError={!!ownerError}
            onChange={(e: any) =>
              onEdit({ owner: { id: e.value, name: e.label } })
            }
            onMenuScrollToBottom={onLoadMoreOrgs}
            handleInputChange={onSearchOrgs}
          />
          <FieldError error={ownerError} />
        </div>
      </div>
      {urlState.show && (
        <div className={rowClasses}>
          <FieldLabel
            htmlFor="account_base_url"
            label={urlState.asSiteName ? t('Site URL') : t('URL')}
            help={
              urlState.asSiteName
                ? t('The name of the EarthRanger site (e.g. my-site).')
                : t('The address Gundi uses to reach this system.')
            }
            required={!urlState.optional}
          />
          <div className="md:w-1/2">
            {urlState.asSiteName ? (
              <EarthRangerSiteInput
                value={url}
                onChange={(e: any) => onEdit({ base_url: e.target.value })}
                error={urlError}
              />
            ) : (
              <>
                <input
                  id="account_base_url"
                  value={url}
                  onChange={(e) => onEdit({ base_url: e.target.value })}
                  className={fieldClasses(urlError)}
                />
                <FieldError error={urlError} />
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountGeneralForm;
